import { Badge, Box, Flex, Input, Text, VStack } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { FaTimes } from "react-icons/fa"

import { ContentService } from "@/client"

interface SubjectSelectorProps {
  selectedSubjects: Array<{ id: string; name: string }>
  onChange: (subjects: Array<{ id: string; name: string }>) => void
  availableSubjects?: Array<{ id: string; name: string }>
}

export function SubjectSelector({
  selectedSubjects,
  onChange,
  availableSubjects,
}: SubjectSelectorProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [showSuggestions, setShowSuggestions] = useState(false)

  // Load all subjects
  const { data: subjectsData } = useQuery({
    queryKey: ["subjects"],
    queryFn: () => ContentService.listSubjects({ limit: 100 }),
    enabled: availableSubjects === undefined,
  })

  const allSubjects = availableSubjects ?? subjectsData?.data ?? []

  // Filter subjects based on search query
  // If no search query, show all available subjects (not already selected)
  const filteredSubjects = allSubjects.filter((subject) => {
    const matchesSearch = searchQuery.trim()
      ? subject.name.toLowerCase().includes(searchQuery.toLowerCase())
      : true
    const notSelected = !selectedSubjects.some(
      (selected) => selected.id === subject.id,
    )
    return matchesSearch && notSelected
  })

  const handleAddSubject = (subject: { id: string; name: string }) => {
    onChange([...selectedSubjects, subject])
    setSearchQuery("")
    setShowSuggestions(false)
  }

  const handleRemoveSubject = (subjectId: string) => {
    onChange(selectedSubjects.filter((subject) => subject.id !== subjectId))
  }

  return (
    <Box width="100%">
      {/* Search Input */}
      <Box position="relative">
        <Input
          placeholder="Search or click to view all subjects..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setShowSuggestions(true)
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") {
              return
            }

            event.preventDefault()
            event.stopPropagation()

            if (filteredSubjects.length > 0) {
              handleAddSubject(filteredSubjects[0])
            }
          }}
          required={false}
        />

        {/* Suggestions Dropdown */}
        {showSuggestions && (
          <Box
            position="absolute"
            top="100%"
            left={0}
            right={0}
            mt={1}
            bg="white"
            _dark={{ bg: "gray.800" }}
            borderWidth="1px"
            borderRadius="md"
            boxShadow="lg"
            maxH="300px"
            overflowY="auto"
            zIndex={10}
          >
            <VStack align="stretch" gap={0}>
              {/* Existing Subjects */}
              {filteredSubjects.length > 0 ? (
                <>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="gray.500"
                    px={3}
                    py={2}
                    borderBottomWidth="1px"
                  >
                    {searchQuery.trim()
                      ? "Matching Subjects"
                      : "Available Subjects"}
                  </Text>
                  {filteredSubjects.map((subject) => (
                    <Box
                      key={subject.id}
                      px={3}
                      py={2}
                      cursor="pointer"
                      _hover={{ bg: "gray.100", _dark: { bg: "gray.700" } }}
                      onClick={() => handleAddSubject(subject)}
                    >
                      <Text>{subject.name}</Text>
                    </Box>
                  ))}
                </>
              ) : (
                <Box px={3} py={2}>
                  <Text color="gray.500">
                    {searchQuery.trim()
                      ? "No matching subjects. Try a different search."
                      : "No subjects available."}
                  </Text>
                </Box>
              )}
            </VStack>
          </Box>
        )}
      </Box>

      {/* Click outside to close */}
      {showSuggestions && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={9}
          onClick={() => setShowSuggestions(false)}
        />
      )}

      {/* Selected Subjects */}
      {selectedSubjects.length > 0 && (
        <Flex gap={2} wrap="wrap" mt={3}>
          {selectedSubjects.map((subject) => (
            <Badge
              key={subject.id}
              colorPalette="blue"
              size="lg"
              py={1}
              px={2}
              display="flex"
              alignItems="center"
              gap={2}
            >
              {subject.name}
              <button
                type="button"
                onClick={() => handleRemoveSubject(subject.id)}
                style={{ cursor: "pointer", opacity: 1 }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = "0.7"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = "1"
                }}
              >
                <FaTimes />
              </button>
            </Badge>
          ))}
        </Flex>
      )}
    </Box>
  )
}
